// The picture store of MODDING (R1918, R2084, ui-map-interface.md §16.3):
// the baked option pictures, kept in the browser over a reload.
//
// A picture is keyed by the identity of the page (R3298), by the look it
// shows (`pictureKey`) and by the pixel scale of the copy. Each row records the build of the client that baked
// it (`__AP_BUILD__`, which `vite.config.mts` stamps on every build and
// every dev server start). A row of another build is KEPT (R2084): the
// store answers it as an old picture, the baker shows it at once and bakes
// the picture again, and the new row replaces it. A new release therefore
// shows the pictures of the last one at once and never keeps them. The
// store keeps at most `[presentation.menu] picture_keep` entries and drops
// the oldest past it.
//
// The store is IndexedDB, because it works on an address with no TLS too
// (a telephone on the local network), where Cache Storage does not. Every
// read and write is wrapped: a browser that refuses the storage (a private
// window, blocked site data, a full disk) answers no picture and keeps
// none, and the baker bakes as it did with no store.

import { IDENTITY } from '../../../identity/pick';

/** The row key of one picture: the identity of the page and the key. A
 *  picture shows the map and the chrome of one identity, so a flip of the
 *  identity bakes the option pictures again (R3298). */
export function rowKey(key: string): string {
  return `${IDENTITY}|${key}`;
}

/** One kept picture as the store answers it: `current` is false for a
 *  picture another build baked. */
export interface StoredPicture {
  blob: Blob;
  current: boolean;
}

/** What the baker needs of a store. A miss and a fault both answer null. */
export interface PictureStore {
  get(key: string): Promise<StoredPicture | null>;
  put(key: string, blob: Blob): Promise<void>;
}

const DB_NAME = 'ap-pictures';
/** Version 1 keyed a row by `build|key`; version 2 keys it by the key
 *  alone and drops the old rows (R2084). */
const DB_VERSION = 2;
const OS = 'pictures';
const BY_AGE = 'at';

/** One kept picture. */
interface Row {
  build: string;
  at: number;
  blob: Blob;
}

/** A request as a promise. */
function done<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** Opens the database, or null where the browser refuses it. */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const r = indexedDB.open(DB_NAME, DB_VERSION);
      r.onupgradeneeded = () => {
        if (r.result.objectStoreNames.contains(OS)) r.result.deleteObjectStore(OS);
        const os = r.result.createObjectStore(OS);
        os.createIndex(BY_AGE, 'at');
      };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Deletes the oldest rows past `keep`. */
async function bound(db: IDBDatabase, keep: number): Promise<void> {
  const os = db.transaction(OS, 'readwrite').objectStore(OS);
  let over = (await done(os.count())) - keep;
  if (over <= 0) return;
  await new Promise<void>((resolve, reject) => {
    const c = os.index(BY_AGE).openCursor();
    c.onerror = () => reject(c.error);
    c.onsuccess = () => {
      const cur = c.result;
      if (cur === null || over <= 0) { resolve(); return; }
      cur.delete();
      over--;
      cur.continue();
    };
  });
}

/**
 * The store of this build, or null where the browser has no IndexedDB or
 * `keep` is 0. The database opens on the first read.
 */
export function browserStore(build: string, keep: number): PictureStore | null {
  if (keep <= 0 || typeof indexedDB === 'undefined') return null;
  let db: Promise<IDBDatabase | null> | null = null;
  const open = (): Promise<IDBDatabase | null> => {
    db ??= openDb();
    return db;
  };
  return {
    async get(key) {
      try {
        const d = await open();
        if (d === null) return null;
        const row = await done(d.transaction(OS, 'readonly').objectStore(OS).get(rowKey(key))) as Row | undefined;
        if (row === undefined || !(row.blob instanceof Blob)) return null;
        return { blob: row.blob, current: row.build === build };
      } catch {
        return null;
      }
    },
    async put(key, blob) {
      try {
        const d = await open();
        if (d === null) return;
        const row: Row = { build, at: Date.now(), blob };
        await done(d.transaction(OS, 'readwrite').objectStore(OS).put(row, rowKey(key)));
        await bound(d, keep);
      } catch {
        // A store that refuses keeps nothing; the picture stands for the page.
      }
    },
  };
}
